/**
 * The inbox socket client: connect, reconnect, refetch.
 *
 * ┌─ ⛔ THE SOCKET IS A HINT, NOT A TRANSPORT ──────────────────────────────────┐
 * │ It says SOMETHING CHANGED; the database says WHAT. Every frame triggers a   │
 * │ refetch of everything materialised, and the frame's contents are not read   │
 * │ except to know one arrived.                                                 │
 * │                                                                            │
 * │ **THE DESIGN DECIDED THIS, NOT THIS FILE.** Three places, and re-litigating │
 * │ it means contradicting all three:                                           │
 * │                                                                            │
 * │   `worker.ts` on a failed broadcast: *"the row is committed but no live     │
 * │   client was told. **A reload will show it; nothing will re-push it.**"*    │
 * │                                                                            │
 * │   `worker.ts` again: *"there is **no sweeper for a push and there should    │
 * │   not be one**, because the inbox reads the database."* A per-client        │
 * │   backlog IS that sweeper.                                                  │
 * │                                                                            │
 * │   `broadcast.ts` on the ids-only payload: *"a client that missed a push and │
 * │   reloads gets the identical result **through one code path rather than     │
 * │   two**."*                                                                  │
 * │                                                                            │
 * │ A reconnect gap is the same event with a different cause. See               │
 * │ `docs/INBOX-WEBSOCKET-DESIGN.md` question 1.                                │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ── ⛔ WHAT LANDS HERE AND WHAT IS STEP 4's, SO NEITHER IS ASSUMED DONE ──────
 *
 * ✅ HERE: the connection state machine, exponential backoff with jitter, the
 *    CONSECUTIVE FAILURE COUNT, and the refetch wiring.
 * ⛔ NOT HERE: the banner. Its threshold is "armed on the second failed
 *    reconnect", and `consecutiveFailures` is the number it reads - but nothing
 *    renders it yet, and the composer's send-disable reads the same state. Both
 *    are step 4. **The state is exported; the UI is absent.**
 */
import { refetchAll } from './query-store.js'
/**
 * ⛔ THE WIRE VOCABULARY, IMPORTED RATHER THAN RESTATED. Added 2026-08-18 after
 * the code audit's D4-1 found `'ready'` written in four places with nothing
 * connecting them - both ends asserted, each against its own copy, so a rename
 * on the server would leave every suite green while this comparison stopped
 * matching. `src/inbox/socket-events.ts` imports nothing, which is what lets a
 * browser module take a VALUE from `src/` without pulling fastify in with it.
 */
import { SOCKET_EVENT_READY } from '../../../src/inbox/socket-events.js'

export type ConnectionState =
  /** No socket yet, or deliberately stopped. */
  | { kind: 'idle' }
  /** A socket is open but has not confirmed its subscription. */
  | { kind: 'connecting' }
  /**
   * The server sent `ready`, which it writes AFTER its Redis SUBSCRIBE landed.
   *
   * ⛔ NOT `onopen`. A websocket opens before the server has subscribed, so a
   * client treating `open` as live has a window where it believes it is
   * receiving and is not - silence that looks like an inbox with nothing new.
   */
  | { kind: 'live' }
  /** Disconnected, retrying. `failures` is what step 4's banner arms on. */
  | { kind: 'reconnecting'; failures: number; nextAttemptInMs: number }

type Options = {
  url?: string
  /** Seam: `ws` in tests, the browser's global otherwise. */
  socketFactory?: (url: string) => WebSocket
  /** Seam, so a test can count refetches without a server. */
  refetch?: () => Promise<unknown>
  /** Seam: deterministic backoff in tests. Real jitter otherwise. */
  jitter?: () => number
  /** Seam: the silence watchdog window. Production uses `SILENCE_TIMEOUT_MS`. */
  silenceMs?: number
  onState?: (state: ConnectionState) => void
}

/** First retry delay, doubling to the cap. */
const BASE_DELAY_MS = 500
const MAX_DELAY_MS = 30_000

/**
 * How long the client will sit in silence before deciding the socket is dead.
 *
 * ┌─ ⛔ WITHOUT THIS, A HALF-OPEN CONNECTION IS INVISIBLE ──────────────────────┐
 * │ A TCP connection dropped by an intermediary sends no close frame and raises │
 * │ no error. `onclose` never fires. The client sits on a socket that will      │
 * │ never deliver anything, reports itself LIVE, and the agent sees an inbox    │
 * │ that looks like nothing is happening.                                       │
 * │                                                                            │
 * │ ⚠️ EVERY TEST BEFORE THIS ONE USED `app.close()`, which sends a proper close │
 * │ frame. **That is the easiest possible reconnect and it proved only the easy │
 * │ path.**                                                                     │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * **Derived from the server's interval rather than chosen.** The server sends a
 * heartbeat every `HEARTBEAT_INTERVAL_MS` (25 s), so silence longer than TWO
 * intervals means two consecutive heartbeats went missing - one lost frame on a
 * lossy link is ordinary and must not tear down a working connection, and "twice
 * in a row" is the same threshold logic the banner uses. The extra 10 s covers
 * RTT (70 ms at the worst measured) and event-loop pauses.
 *
 * ⚠️ **IT IS A CLIENT-SIDE COPY OF A SERVER-SIDE NUMBER, WHICH IS A REAL COUPLING.**
 * They are in different bundles and nothing enforces the relationship. Raising
 * the server's interval past 30 s without raising this makes every client
 * reconnect on a loop, and the symptom would look like a network problem.
 * `tests/inbox-heartbeat.test.ts` asserts the relationship rather than trusting
 * it.
 */
export const SILENCE_TIMEOUT_MS = 60_000

export type InboxSocket = {
  stop: () => void
  state: () => ConnectionState
  /** Total refetch passes this socket has triggered. Test seam and diagnostic. */
  refetches: () => number
  /**
   * How many times the silence watchdog fired.
   *
   * ⛔ **A DETECTOR THAT SILENTLY STOPS IS ITSELF A HALF-OPEN DETECTOR.** This is
   * how a test proves the watchdog RAN, rather than that the socket happened to
   * reconnect for some other reason - the same distinction as fail2ban loaded
   * versus fail2ban filtering.
   */
  silenceTimeouts: () => number
}

export function startInboxSocket(options: Options = {}): InboxSocket {
  const url = options.url ?? inboxSocketUrl()
  const make = options.socketFactory ?? ((u: string) => new WebSocket(u))
  const refetch = options.refetch ?? refetchAll
  const jitter = options.jitter ?? Math.random
  const silenceMs = options.silenceMs ?? SILENCE_TIMEOUT_MS

  let state: ConnectionState = { kind: 'idle' }
  let socket: WebSocket | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let failures = 0
  let stopped = false
  let refetches = 0
  let silenceTimer: ReturnType<typeof setTimeout> | null = null
  let silenceTimeouts = 0

  /**
   * Re-arm the silence watchdog. Called on EVERY frame, heartbeat or not, so a
   * busy conversation needs no extra traffic to stay trusted.
   *
   * ⛔ ARMED FROM THE FRAME HANDLER RATHER THAN ON A SCHEDULE. A watchdog that
   * ran on its own timer and merely checked a timestamp would still be running
   * if frames stopped; this one is only ever reset by evidence of life, so its
   * default outcome is "dead" and something has to actively prove otherwise.
   */
  /**
   * Give up on the current socket without waiting for the transport to agree.
   *
   * ┌─ ⛔ THE FIRST VERSION CALLED `socket.close()` AND WAITED FOR `onclose` ────┐
   * │ That was wrong in precisely the case the watchdog exists for. `close()`   │
   * │ starts a CLOSING HANDSHAKE: it sends a close frame and waits for the      │
   * │ peer's reply. **On a half-open connection the peer is unreachable, so the │
   * │ reply never comes** and `onclose` does not fire until the implementation's │
   * │ own timeout - 30 s in `ws`, and browser-dependent elsewhere.              │
   * │                                                                          │
   * │ ⚠️ FOUND BY THE HALF-OPEN TEST, NOT BY READING. The watchdog fired         │
   * │ correctly and the client STAYED `live` for the whole test, because every  │
   * │ state transition hung off `onclose`. The detector worked and the thing it │
   * │ notified did not - a working detector wired to a dead consumer, which     │
   * │ looks identical to no detector at all.                                     │
   * │                                                                          │
   * │ ✅ The watchdog has already CONCLUDED the socket is dead. Waiting for the  │
   * │ transport to confirm is the same dependency that just failed. So the      │
   * │ handlers are detached first, the state moves immediately, and `close()`   │
   * │ is best-effort cleanup whose completion nothing depends on.               │
   * └──────────────────────────────────────────────────────────────────────────┘
   */
  const abandon = (): void => {
    const dead = socket
    socket = null
    if (silenceTimer) clearTimeout(silenceTimer)
    silenceTimer = null
    if (dead) {
      // Detached BEFORE close(), so the eventual `onclose` cannot count a second
      // failure and double the backoff for one disconnection.
      dead.onmessage = null
      dead.onclose = null
      dead.onerror = null
      try {
        dead.close()
      } catch {
        // A close on an already-broken socket can throw. It is being abandoned.
      }
    }
    scheduleReconnect()
  }

  const armSilenceWatchdog = (): void => {
    if (silenceTimer) clearTimeout(silenceTimer)
    if (stopped) return
    silenceTimer = setTimeout(() => {
      silenceTimeouts += 1
      abandon()
    }, silenceMs)
    // Never hold a process open for this. Harmless in a browser, load-bearing
    // in the tests, which run in node.
    ;(silenceTimer as { unref?: () => void }).unref?.()
  }

  const setState = (next: ConnectionState): void => {
    state = next
    options.onState?.(next)
  }

  /**
   * ┌─ ⛔ COALESCING, AND WHY IT IS NOT A DEBOUNCE ─────────────────────────────┐
   * │ Every frame triggers a refetch, so a burst of twenty messages would       │
   * │ otherwise mean twenty refetches of every materialised query.              │
   * │                                                                          │
   * │ A TIME-BASED DEBOUNCE IS THE WRONG INSTRUMENT TWICE OVER. It needs a      │
   * │ window nobody can choose correctly, and its trailing edge is exactly      │
   * │ where the classic defect lives: **the last event gets dropped**, which    │
   * │ here means the newest message never appears - the precise failure the     │
   * │ whole refetch design exists to prevent.                                   │
   * │                                                                          │
   * │ ✅ IN-FLIGHT PLUS DIRTY INSTEAD, AND IT IS SELF-CLOCKING. If a refetch is  │
   * │ running when a frame arrives, mark dirty; when it finishes, if dirty, run  │
   * │ EXACTLY ONE more. So:                                                     │
   * │                                                                          │
   * │   - at most one refetch in flight and one queued, never a pile-up;        │
   * │   - **the last frame can never be dropped**, because any frame arriving    │
   * │     during a pass guarantees another pass after it;                        │
   * │   - the rate is bounded by how long a refetch TAKES rather than by how     │
   * │     fast messages arrive, so it coalesces exactly as hard as it needs to   │
   * │     and no harder. No window to tune, and none to get wrong.               │
   * └──────────────────────────────────────────────────────────────────────────┘
   */
  let inFlight = false
  let dirty = false
  const coalescedRefetch = (): void => {
    if (inFlight) {
      dirty = true
      return
    }
    inFlight = true
    void (async () => {
      try {
        do {
          dirty = false
          await refetch()
          refetches += 1
          // Loop rather than recurse: a long burst would otherwise grow the
          // stack by one frame per message.
        } while (dirty && !stopped)
      } finally {
        inFlight = false
      }
    })()
  }

  const scheduleReconnect = (): void => {
    if (stopped) return
    failures += 1
    /**
     * Exponential with FULL JITTER. Without jitter every client that dropped
     * during one outage returns in lockstep, which is a thundering herd against
     * a service that has just come back.
     *
     * ✅ THE CODE IS UNCHANGED AND WAS ALREADY RIGHT; ONLY ITS REASON MOVED.
     * Until 2026-08-07 this added "at fewer than five agents that is not today's
     * problem, so it is here for the reason it is cheap rather than because it is
     * urgent." The product is now deployed per customer at one agent or twenty-
     * plus, so a herd is a real failure mode rather than a hypothetical one.
     *
     * ⚠️ WORTH NOTING FOR THE NEXT DECISION OF THIS KIND: built cheap-and-early,
     * this needed no work when the premise went. The set corrected the same day
     * that DID need work is the set where the count was the whole argument.
     */
    const ceiling = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** (failures - 1))
    const delay = Math.round(ceiling * jitter())
    setState({ kind: 'reconnecting', failures, nextAttemptInMs: delay })
    timer = setTimeout(open, delay)
  }

  function open(): void {
    if (stopped) return
    setState(failures === 0 ? { kind: 'connecting' } : state)

    let ws: WebSocket
    try {
      ws = make(url)
    } catch {
      // A constructor that throws (a bad URL, a blocked scheme) must still
      // retry rather than leaving the client silently dead.
      scheduleReconnect()
      return
    }
    socket = ws

    ws.onmessage = (event: MessageEvent): void => {
      /**
       * ⚠️ THE FRAME'S CONTENT IS READ FOR ONE THING: whether it is `ready`.
       * Everything else triggers a refetch WITHOUT being parsed for meaning,
       * because the payload is ids only and the database is the answer. A
       * client that read the conversation id and refetched only that
       * conversation would be the narrow refetch, which is wrong in exactly the
       * case that matters.
       */
      let isReady = false
      try {
        isReady =
          (JSON.parse(String(event.data)) as { event?: string }).event === SOCKET_EVENT_READY
      } catch {
        // Unparseable frames are still a signal that something happened.
      }

      // ⛔ ANY FRAME IS EVIDENCE OF LIFE, so the watchdog is re-armed before
      // anything else is decided about this frame.
      armSilenceWatchdog()

      if (isReady) {
        failures = 0
        setState({ kind: 'live' })
      }
      // ⛔ REFETCH ON `ready` TOO, NOT ONLY ON MESSAGES. A reconnect's whole
      // purpose is to recover what was missed WHILE DISCONNECTED, and no frame
      // announces those - they were published to a channel nobody was on.
      coalescedRefetch()
    }

    ws.onclose = (): void => {
      socket = null
      if (silenceTimer) clearTimeout(silenceTimer)
      silenceTimer = null
      scheduleReconnect()
    }

    ws.onerror = (): void => {
      // `onerror` is followed by `onclose` in every implementation, so
      // reconnecting here as well would double-count the failure and double the
      // backoff. Closing is left to `onclose`.
    }
  }

  open()

  return {
    stop: () => {
      stopped = true
      if (timer) clearTimeout(timer)
      timer = null
      if (silenceTimer) clearTimeout(silenceTimer)
      silenceTimer = null
      socket?.close()
      socket = null
      setState({ kind: 'idle' })
    },
    state: () => state,
    refetches: () => refetches,
    silenceTimeouts: () => silenceTimeouts,
  }
}

/**
 * Same origin, `ws`/`wss` matching the page's scheme.
 *
 * ⚠️ `location` IS REACHED THROUGH A NARROW STRUCTURAL TYPE rather than the DOM
 * lib, because this module is compiled by TWO tsconfigs: `web/tsconfig.json`
 * (which has `lib: dom`) and `tsconfig.test.json` (which does not, being a node
 * program). Depending on the DOM lib here compiles under one and fails under the
 * other, and the one it fails under is the one CI runs at step 8.
 */
/**
 * ⛔ THE `?? 'localhost'` BELOW IS FAIL-OPEN AND IT IS A RECORDED FINDING.
 * `socket-url-localhost-fallback` in `ops/deferrals.txt`, owner INBOX.
 *
 * A missing `location` produces a connection to *somewhere* rather than a
 * refusal - the same shape as `web/vite.config.ts` defaulting to production and
 * `check-socket-subscribers.sh` defaulting to Redis database 0, both of which
 * were changed to refuse. This one has not been, and it is live client code.
 *
 * ⚠️ It has never been taken: a browser always has a location. It becomes
 * reachable the moment there is a client that is not a browser, which MOBILE now
 * is - the next development rather than a future workstream.
 */
export function inboxSocketUrl(): string {
  const loc = (globalThis as { location?: { protocol?: string; host?: string } }).location
  /**
   * ⛔ IT REFUSES RATHER THAN DEFAULTING, SINCE 2026-08-11. The fallback was
   * `?? 'localhost'`, which is the general remedy applied here: **when the right
   * answer is unavailable, reduce what the surface does until it is available -
   * do not ship the wrong answer and record a gap.**
   *
   * ⚠️ A DEFAULT IS NOT THE SAFE CHOICE HERE, WHICH IS THE WHOLE POINT. Failing
   * to connect is loud, immediate, and shows the agent the banner this phase
   * built. Connecting to `localhost` on a client with no `location` is silent:
   * on a phone it reaches the device itself, so the socket never opens and
   * nothing says why - and if anything IS listening there, it is worse than
   * nothing. The same change was already made to `web/vite.config.ts` (was
   * defaulting to production) and `check-socket-subscribers.sh` (was defaulting
   * to Redis database 0). This was the last of the three.
   *
   * ⛔ AND THE THROW IS REACHABLE ONLY BY THE CLIENT THAT DOES NOT EXIST YET. A
   * browser always has a `location`, so nothing changes for anything shipping
   * today; MOBILE is what makes it reachable, and it should meet a message
   * naming the fix rather than an inexplicable dead socket.
   */
  if (loc?.host === undefined || loc.host === '') {
    throw new Error(
      'inboxSocketUrl(): no location.host is available, so there is no server to connect to. ' +
        'A non-browser client must pass an explicit url - see `startInboxSocket({ url })`. ' +
        'This deliberately refuses rather than defaulting to localhost, which on a device ' +
        'means the device itself.',
    )
  }
  const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${loc.host}/inbox/socket`
}
